import { ActKc2GroupTypeEnum, BimActKc2GroupTypeEnum, SortEnumType } from 'gql/graphql';
import type { ActKc2ListItemData, BimActKc2ListItemData, Maybe, SkzsActKc2StatusEnum } from 'gql/graphql';
import { getDateParts } from 'utils/formatDateToLocale';

import { ActKC2CostGroup, getSkzsActKc2StatusTitle } from '../../types';

export interface TOrderState {
    [key: string]: SortEnumType | TOrderState;
}

const getAdditionalValuesByGroup = (
    groupType: Maybe<ActKc2GroupTypeEnum | BimActKc2GroupTypeEnum> | undefined,
): TOrderState[] => {
    if (groupType === ActKc2GroupTypeEnum.ByYear || groupType === BimActKc2GroupTypeEnum.ByYear) {
        return [
            {
                actYear: SortEnumType.Desc,
            },
        ];
    }

    if (groupType === ActKc2GroupTypeEnum.ByMonth || groupType === BimActKc2GroupTypeEnum.ByMonth) {
        return [
            {
                actYear: SortEnumType.Desc,
            },
            {
                actMonth: SortEnumType.Desc,
            },
        ];
    }

    return [];
};

export const getOrderValues = (
    sortType: Maybe<'asc' | 'desc'> | undefined,
    colId: string | undefined,
    groupType: Maybe<ActKc2GroupTypeEnum | BimActKc2GroupTypeEnum> | undefined,
    prevState: TOrderState | TOrderState[] | undefined,
): TOrderState | TOrderState[] | undefined => {
    if (prevState && !colId) {
        return undefined;
    }

    const getColId = (id: string) => (id === 'number' ? 'fullNumber' : id);

    if (colId) {
        if (colId === 'constructionType.name') {
            const [mainId, slaveId] = colId.split('.');

            return {
                [mainId]: {
                    [slaveId]: sortType === 'asc' ? SortEnumType.Asc : SortEnumType.Desc,
                },
            };
        }

        return [
            ...getAdditionalValuesByGroup(groupType),
            {
                [getColId(colId)]: sortType === 'asc' ? SortEnumType.Asc : SortEnumType.Desc,
            },
        ];
    }

    return prevState;
};

export const groupTypeNameGetter = (
    item: ActKc2ListItemData | BimActKc2ListItemData,
    groupType: ActKc2GroupTypeEnum | BimActKc2GroupTypeEnum,
    groupItemsCount?: number,
): string => {
    switch (groupType) {
        case ActKc2GroupTypeEnum.ByConstructionType:
        case BimActKc2GroupTypeEnum.ByConstructionType:
            return `${item?.constructionType?.name} (${item?.groupCount})`;

        case ActKc2GroupTypeEnum.ByCostGroup:
        case BimActKc2GroupTypeEnum.ByCostGroup:
            return `${ActKC2CostGroup[item.costGroup]} (${item.groupCount})`;

        case ActKc2GroupTypeEnum.ByMonth:
        case BimActKc2GroupTypeEnum.ByMonth: {
            if (!item.actDate) {
                return 'Акты без даты';
            }
            const { year, month } = getDateParts(item.actDate);

            return `Акты за ${month ?? '_'} ${year ?? '_'} (${groupItemsCount})`;
        }

        case ActKc2GroupTypeEnum.ByObject:
        case BimActKc2GroupTypeEnum.ByObject:
            return `${item.workOrderObjectName ?? '_'} (${item.groupCount})`;

        case ActKc2GroupTypeEnum.ByYear:
        case BimActKc2GroupTypeEnum.ByYear: {
            if (!item.actDate) {
                return 'Акты без даты';
            }

            return `Акты за ${getDateParts(item.actDate).year ?? '_'} год (${groupItemsCount})`;
        }

        case ActKc2GroupTypeEnum.Custom:
        case BimActKc2GroupTypeEnum.Custom:
            return `Акты ${item.groupName ?? '_'} (${item.groupCount})`;

        case ActKc2GroupTypeEnum.ByStatusSkzs:
            return `${getSkzsActKc2StatusTitle((item as ActKc2ListItemData).statusSKzS, true)} (${item.groupCount})`;

        default:
            return '';
    }
};

export const groupIdGetter = <
    T extends {
        groupId?: any;
        actDate?: Date;
        groupUniqueId?: string | null;
        statusSKzS?: Maybe<SkzsActKc2StatusEnum> | undefined;
    },
>(
    item: T,
    groupType: ActKc2GroupTypeEnum | BimActKc2GroupTypeEnum,
): string | number => {
    if (groupType === ActKc2GroupTypeEnum.Custom || groupType === BimActKc2GroupTypeEnum.Custom) {
        return item.groupId;
    }

    if (groupType === ActKc2GroupTypeEnum.ByMonth || groupType === BimActKc2GroupTypeEnum.ByMonth) {
        if (!item.actDate) {
            return 'Акты без даты';
        }
        const { year, month } = getDateParts(item.actDate);

        return `${month}-${year}`;
    }

    if (groupType === ActKc2GroupTypeEnum.ByYear || groupType === BimActKc2GroupTypeEnum.ByYear) {
        if (!item.actDate) {
            return 'Акты без даты';
        }
        const { year } = getDateParts(item.actDate);

        return year;
    }

    if (groupType === ActKc2GroupTypeEnum.ByStatusSkzs) {
        return item.statusSKzS as string;
    }

    return item.groupUniqueId ?? '';
};

export type TGroupedData<TData, TGroup> = Partial<{ isGroup: boolean; groupType: TGroup } & TData>;
type TGetGroupByType = {
    (
        items: BimActKc2ListItemData[],
        groupType: BimActKc2GroupTypeEnum,
        sortSubgroups?: boolean,
    ): FlatArray<TGroupedData<BimActKc2ListItemData, BimActKc2GroupTypeEnum>[][], 1>[];
    (
        items: ActKc2ListItemData[],
        groupType: ActKc2GroupTypeEnum,
        sortSubgroups?: boolean,
    ): FlatArray<TGroupedData<ActKc2ListItemData, ActKc2GroupTypeEnum>[][], 1>[];
};

export const getGroupByType: TGetGroupByType = (
    items: (BimActKc2ListItemData | ActKc2ListItemData)[],
    groupType: BimActKc2GroupTypeEnum | ActKc2GroupTypeEnum,
    sortSubgroups,
) => {
    const groupedItemsMap = new Map<string | number, TGroupedData<any, any>[]>();

    items.forEach((item) => {
        const groupId = groupIdGetter(item, groupType);

        if (groupId) {
            if (!groupedItemsMap.has(groupId)) {
                groupedItemsMap.set(groupId, [
                    {
                        isGroup: true,
                        groupType,
                        groupId,
                        actDate: item.actDate,
                        number: groupTypeNameGetter(item, groupType),
                        groupCostWithoutTax: item.groupCostWithoutTax,
                    },
                ]);
            }
            const currentGroup = groupedItemsMap.get(groupId);

            if (currentGroup) {
                currentGroup.push(item);
                currentGroup[0].number = groupTypeNameGetter(item, groupType, currentGroup.length - 1);
            }
        }
    });

    const resultArray = Array.from(groupedItemsMap.values());

    return sortSubgroups
        ? resultArray.sort((a: any, b: any) => String(a?.[0]?.number).localeCompare(String(b?.[0].number))).flat()
        : resultArray.flat();
};
